/**
 * MAISON — COLLECTION GRID SWITCHER
 *
 * Two buttons that change how many columns the product grid uses. The count is written as a CSS
 * custom property on the grid, so switching is a single style write with no re-render and no
 * request — the cards simply reflow.
 *
 * The choice is remembered per browser in localStorage, so paginating or filtering (both of which
 * replace the section's markup) does not silently drop the shopper back to the default.
 */
(() => {
  if (customElements.get('maison-collection-grid')) {
    return;
  }

  const DESKTOP = '(min-width: 1000px)';

  customElements.define(
    'maison-collection-grid',
    class MaisonCollectionGrid extends HTMLElement {
      connectedCallback() {
        this.buttons = [...this.querySelectorAll('[data-columns]')];

        if (!this.buttons.length) {
          return;
        }

        /*
          Each instance owns exactly one breakpoint: the mobile group and the desktop group are
          separate elements with their own scope, their own column options and their own storage
          key. That separation is the whole point — a choice made on a phone must not follow the
          shopper to a desktop width, and vice versa.
        */
        this.scope = this.dataset.scope === 'mobile' ? 'mobile' : 'desktop';
        this.storageKey = `maison:collection-columns:${this.scope}`;
        this.media = window.matchMedia(DESKTOP);

        this.onBreakpoint = this.onBreakpoint.bind(this);
        this.media.addEventListener('change', this.onBreakpoint);

        /*
          The grid is found from the section root rather than from this element, because the
          switcher sits in the top bar and the grid is further down a different branch.
        */
        this.section = this.closest('.shopify-section, [id^="shopify-section-"]') || document;
        this.grid = this.section.querySelector('.maison-collection__grid');

        if (!this.grid) {
          return;
        }

        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        this.restore();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.media?.removeEventListener('change', this.onBreakpoint);
      }

      // Whether this instance's breakpoint is the one currently showing.
      get isActive() {
        return this.scope === 'desktop' ? this.media.matches : !this.media.matches;
      }

      /*
        On crossing the breakpoint, the group that has just become active re-applies its own
        stored choice, and the one that has just become inactive clears the override it wrote.

        Without the clear, the inline value would linger on the grid and keep overriding the CSS
        variable for the *other* breakpoint — which is exactly the cross-contamination this
        design avoids. Clearing hands control back to the media query, and the newly active group
        then writes its own value.

        Both groups receive this event and the order is not guaranteed, so the deactivating one
        only clears a value it recognises as its OWN. Clearing unconditionally would wipe the
        value the newly active group had just written if it happened to run second.
      */
      onBreakpoint() {
        if (this.isActive) {
          this.restore();
          return;
        }

        const written = this.grid.style.getPropertyValue('--maison-collection-columns');

        if (written && written === this.current) {
          this.grid.style.removeProperty('--maison-collection-columns');
        }

        this.current = null;
      }

      onClick(event) {
        const button = event.target.closest('[data-columns]');

        if (!button || !this.buttons.includes(button)) {
          return;
        }

        this.apply(button.dataset.columns, true);

        /*
          localStorage throws outright in some contexts (Safari private mode, blocked site data),
          and a failed *preference* write must never break the switch itself.
        */
        try {
          localStorage.setItem(this.storageKey, button.dataset.columns);
        } catch {
          // Preference not persisted; the switch still works for this page view.
        }
      }

      /*
        Applies a column count: the grid's variable and the buttons' pressed state.

        The variable is set on the GRID, not the section, so it wins over the section-level
        `--maison-collection-columns` that the media query defines without having to out-specify
        it. An inactive group never writes, so the hidden breakpoint's preference cannot affect
        what is on screen.
      */
      apply(columns, animate = false) {
        if (!columns || !this.isActive) {
          return;
        }

        this.grid.style.setProperty('--maison-collection-columns', columns);
        this.current = columns;

        for (const button of this.buttons) {
          button.setAttribute('aria-pressed', String(button.dataset.columns === columns));
        }

        if (animate) {
          this.reveal();
        }
      }

      /*
        Re-runs the theme's reveal on the cards, so a grid change lands with the same staggered
        fade-and-lift as the initial load.

        Impact's own <reveal-items> cannot be reused for this: it fires once from its constructor
        via inView(), and the method that animates is a #private class field, so there is no way
        to ask it to run again. The values below therefore mirror theme.js's reveal exactly
        (opacity 0 -> 1, a 15px lift, 350ms, 50ms stagger) so the two are indistinguishable.

        It also honours the same two conditions the theme checks, so this never animates where the
        theme itself would not: the shopper's reduced-motion preference, and the merchant's
        "stagger products apparition" setting.
      */
      reveal() {
        const allowed =
          window.matchMedia('(prefers-reduced-motion: no-preference)').matches &&
          window.themeVariables?.settings?.staggerProductsApparition;

        if (!allowed) {
          return;
        }

        const cards = this.grid.children;

        /*
          The stagger is capped: a full page of 24+ cards at 50ms each would run for over a
          second, so the delay stops climbing after this many cards and the tail all lands
          together. The theme's own reveal is bounded in practice by the viewport, since it only
          fires on scroll-in — a grid change re-animates everything at once, so the cap is needed
          here and not there.
        */
        const maxStagger = 12;

        for (let i = 0; i < cards.length; i += 1) {
          cards[i].animate(
            {
              opacity: [0, 1],
              transform: ['translateY(15px)', 'translateY(0)'],
            },
            {
              duration: 350,
              delay: Math.min(i, maxStagger) * 50,
              easing: 'ease-out',
              fill: 'both',
            }
          );
        }
      }

      /*
        Re-applies the stored choice. Only a value one of the buttons actually offers is honoured —
        a stale number from an earlier setting would otherwise set a column count the merchant has
        since changed away from.
      */
      restore() {
        let stored = null;

        try {
          stored = localStorage.getItem(this.storageKey);
        } catch {
          // No stored preference available; fall through to the markup's default.
        }

        if (!stored || !this.buttons.some((button) => button.dataset.columns === stored)) {
          return;
        }

        /*
          aria-pressed is synced even while this group is hidden, so the buttons already show the
          right state the moment its breakpoint becomes visible. Only the grid write is gated on
          being active.
        */
        for (const button of this.buttons) {
          button.setAttribute('aria-pressed', String(button.dataset.columns === stored));
        }

        this.apply(stored);
      }
    }
  );
})();
